import StatCard from '@/components/dashboard/StatCard';
import { MonthlyOrderMetricsResult } from '@/models/OrderMetrics';
import { formatPrice } from '@/utils/currency';
import { ChartBarIcon } from '@heroicons/react/24/solid';

interface MonthlyOrderMetricsStatsProps {
  monthly?: MonthlyOrderMetricsResult;
  isLoading: boolean;
  currencyCode?: string;
  limit?: number;
}

const MonthlyOrderMetricsStats = ({
  monthly,
  isLoading,
  currencyCode,
  limit = 5,
}: MonthlyOrderMetricsStatsProps) => {
  const items =
    monthly?.summary
      .slice()
      .sort((a, b) => {
        const aDate = new Date(a.year, a.month - 1).getTime();
        const bDate = new Date(b.year, b.month - 1).getTime();
        return bDate - aDate;
      })
      .slice(0, limit) ?? [];

  return (
    <StatCard
      icon={<ChartBarIcon className="h-8 w-8 text-slate-400 mt-1" />}
      loading={false}
      value={
        <div className="overflow-x-auto">
          <table className="w-full min-w-full text-sm">
            <thead className="border-b text-gray-700 font-medium">
              <tr>
                <th className="py-2 text-left">Month</th>
                <th className="py-2 text-right">Orders</th>
                <th className="py-2 text-right">Amount</th>
              </tr>
            </thead>

            <tbody>
              {isLoading &&
                Array.from({ length: limit }).map((_, i) => (
                  <tr key={`loading-${i}`} className="border-b last:border-none">
                    <td className="py-3">
                      <div className="h-4 bg-gray-200 animate-pulse rounded w-28" />
                    </td>
                    <td className="py-3 text-right">
                      <div className="h-4 bg-gray-200 animate-pulse rounded w-10 ml-auto" />
                    </td>
                    <td className="py-3 text-right">
                      <div className="h-4 bg-gray-200 animate-pulse rounded w-20 ml-auto" />
                    </td>
                  </tr>
                ))}

              {!isLoading &&
                items.map((m) => {
                  const date = new Date(m.year, m.month - 1, 1);
                  const label = date.toLocaleString('en-GB', {
                    month: 'short',
                    year: 'numeric',
                  });

                  return (
                    <tr key={label} className="border-b last:border-none">
                      <td className="py-2">{label}</td>
                      <td className="py-2 text-right">{m.orderCount.toLocaleString()}</td>
                      <td className="py-2 text-right">
                        {formatPrice(m.orderAmount, currencyCode)}
                      </td>
                    </tr>
                  );
                })}

              {!isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={3} className="py-3 text-center text-gray-500 italic">
                    No recent orders
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      }
    />
  );
};

export default MonthlyOrderMetricsStats;
